import React, { useState } from "react";
import {
  Dialog,
  IconButton,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { withFirebase } from "../Firebase";
import { useError } from "../ErrorProvider";
import { getHandoutUploadPayload } from "./handoutUpload";

const EditCompanyDialog = withFirebase(({ firebase, company, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [winnerCount, setWinnerCount] = useState(1);
  const [quarterlyWinnerCount, setQuarterlyWinnerCount] = useState(1);
  const [handoutFile, setHandoutFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { showError } = useError();

  const normalizeWinnerCount = (value) => Math.max(1, Number(value) || 1);

  const handleOpen = () => {
    setCompanyName(company.CompanyName || "");
    setEmails(
      Array.isArray(company.ContactEmail) ? [...company.ContactEmail] : []
    );
    setNewEmail("");
    setWinnerCount(normalizeWinnerCount(company.winnerCount));
    setQuarterlyWinnerCount(normalizeWinnerCount(company.quarterlyWinnerCount));
    setHandoutFile(null);
    setOpen(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (!file) {
      setHandoutFile(null);
      return;
    }

    if (file.type === "application/pdf") {
      setHandoutFile(file);
      return;
    }

    showError("Only PDF files are accepted.");
    event.target.value = null;
  };

  const handleAddEmail = () => {
    if (newEmail && !emails.includes(newEmail)) {
      setEmails([...emails, newEmail]);
      setNewEmail("");
    }
  };

  const handleRemoveEmail = (email) => {
    setEmails(emails.filter((e) => e !== email));
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      await firebase.updateCompany({
        companyCode: company.id,
        companyName,
        contactEmails: emails,
        winnerCount: normalizeWinnerCount(winnerCount),
        quarterlyWinnerCount: normalizeWinnerCount(quarterlyWinnerCount),
      });

      if (handoutFile) {
        const uploadPayload = await getHandoutUploadPayload(handoutFile);
        await firebase.uploadCompanyHandout({
          companyCode: company.id,
          ...uploadPayload,
        });
      }

      if (typeof onSuccess === "function") onSuccess();
      setOpen(false);
    } catch (err) {
      showError(`Error updating company: ${err}`);
    } finally {
      setSubmitting(false);
    }
  };

  const valid = () => companyName && emails.length > 0;

  return (
    <>
      <IconButton onClick={handleOpen} size="small">
        <EditIcon fontSize="small" />
      </IconButton>
      <Dialog
        open={open}
        aria-labelledby="edit-dialog-title"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="edit-dialog-title">Edit Company</DialogTitle>
        <DialogContent>
          <TextField
            value={companyName}
            margin="dense"
            label="Company Name"
            type="text"
            onChange={(e) => setCompanyName(e.target.value)}
            fullWidth
          />
          <TextField
            value={newEmail}
            margin="dense"
            label="Add HR Email"
            type="email"
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleAddEmail()}
            fullWidth
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={handleAddEmail}
                  size="small"
                  disabled={!newEmail}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              ),
            }}
          />
          <TextField
            value={winnerCount}
            margin="dense"
            label="Weekly Winner Count"
            type="number"
            onChange={(e) => setWinnerCount(e.target.value)}
            inputProps={{ min: 1 }}
            fullWidth
          />
          <TextField
            value={quarterlyWinnerCount}
            margin="dense"
            label="Quarterly Winner Count"
            type="number"
            onChange={(e) => setQuarterlyWinnerCount(e.target.value)}
            inputProps={{ min: 1 }}
            fullWidth
          />
          <List dense>
            {emails.map((email) => (
              <ListItem key={email}>
                <ListItemText primary={email} />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handleRemoveEmail(email)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          <div style={{ marginTop: 16 }}>
            <Typography variant="caption" color="textSecondary">
              Replace Handout (PDF)
            </Typography>
            <input
              type="file"
              accept="application/pdf"
              style={{ display: "block", marginTop: 4 }}
              onChange={handleFileChange}
            />
            {handoutFile ? (
              <Typography variant="caption" color="textSecondary">
                Selected: {handoutFile.name}
              </Typography>
            ) : null}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            color="primary"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={!valid() || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default EditCompanyDialog;
